import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/shared/Layout';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import EmailVerification from './components/auth/EmailVerification';
import VerifyEmailPrompt from './components/auth/VerifyEmailPrompt';
import Dashboard from './components/dashboard/Dashboard';
import ProfileSettings from './components/profile/ProfileSettings';
import ProfileHistory from './components/profile/ProfileHistory';
import BranchManagement from './components/branch/BranchManagement';
import SuperAdminDashboard from './components/super-admin/SuperAdminDashboard';
import BusinessDetailsPage from './components/super-admin/BusinessDetailsPage';
import RequestPasswordReset from './components/auth/RequestPasswordReset';
import ResetPassword from './components/auth/ResetPassword';
import ChangePassword from './components/auth/ChangePassword';
import UserManagement from './components/users/UserManagement';
import BranchDetails from './components/branch/BranchDetails';
import UserBranchManagement from './components/users/UserBranchManagement';
import BranchUserManagement from './components/branch/BranchUserManagement';
import ProductManagement from './components/products/ProductManagement';
import CategoryManagement from './components/products/CategoryManagement';
import BranchInventory from './components/branch/BranchInventory';
import InventoryManagement from './components/inventory/InventoryManagement';
import SalesInterface from './components/sales/SalesInterface';
import TermsAndPrivacy from './components/terms/TermsAndPrivacy';
import Contact from './components/terms/Contact';
import TermsAcceptance from './components/auth/TermsAcceptance';
import LandingPage from './components/landing/LandingPage';
import ArticlePage from './components/landing/ArticlePage';
import ErrorBoundary from './components/shared/ErrorBoundary';
import { NotificationProvider } from './contexts/NotificationContext';
import './App.css';

// Public Route component - redirects authenticated users to dashboard
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return null;
  }

  // Redirect authenticated users to their appropriate dashboard
  if (user) {
    if (user.isSuperAdmin) {
      return <Navigate to="/super-admin" />;
    }
    return <Navigate to="/app/dashboard" />;
  }

  return children;
};

// Protected Route component
const ProtectedRoute = ({ children, roles = [], requireSuperAdmin = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return null; // or a loading spinner
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Super admin check
  if (requireSuperAdmin && !user.isSuperAdmin) {
    return <Navigate to="/app/dashboard" replace state={{ accessDenied: true }} />;
  }

  if (roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to="/app/dashboard" replace state={{ accessDenied: true }} />;
  }

  if (!user.emailVerified) {
    return <Navigate to="/verify-email" />;
  }

  // Enforce first time login password change (skip for super admins)
  if (user.isFirstLogin && !user.isSuperAdmin) {
    return <Navigate to="/change-password" />;
  }

  // Enforce terms acceptance (skip for super admins)
  if (user.termsAccepted === false && !user.isSuperAdmin) {
    return <Navigate to="/terms-acceptance" />;
  }

  return children;
};

function App() {
  const theme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#6366F1',      // Indigo - modern, professional
        light: '#818CF8',
        dark: '#4F46E5',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#EC4899',      // Pink accent
        light: '#F472B6',
        dark: '#DB2777',
        contrastText: '#ffffff',
      },
      background: {
        default: '#F8FAFC',   // Slightly cool gray
        paper: '#ffffff',
      },
      text: {
        primary: '#1E293B',   // Slate 800 - excellent readability
        secondary: '#64748B', // Slate 500
      },
      error: {
        main: '#EF4444',
        light: '#FCA5A5',
        dark: '#DC2626',
      },
      warning: {
        main: '#F59E0B',
        light: '#FCD34D',
        dark: '#D97706',
      },
      info: {
        main: '#3B82F6',
        light: '#93C5FD',
        dark: '#2563EB',
      },
      success: {
        main: '#10B981',
        light: '#6EE7B7',
        dark: '#059669',
      },
      divider: '#E2E8F0',
      grey: {
        50: '#F8FAFC',
        100: '#F1F5F9',
        200: '#E2E8F0',
        300: '#CBD5E1',
        400: '#94A3B8',
        500: '#64748B',
        600: '#475569',
        700: '#334155',
        800: '#1E293B',
        900: '#0F172A',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.25rem',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '1.875rem',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.3,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.4,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
      },
      button: {
        fontWeight: 600,
        letterSpacing: '0.01em',
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: [
      'none',
      '0px 1px 2px rgba(15, 23, 42, 0.06)',
      '0px 1px 3px rgba(15, 23, 42, 0.1)',
      '0px 2px 4px rgba(15, 23, 42, 0.1)',
      '0px 4px 6px rgba(15, 23, 42, 0.1)',
      '0px 6px 8px rgba(15, 23, 42, 0.1)',
      '0px 8px 12px rgba(15, 23, 42, 0.1)',
      '0px 12px 16px rgba(15, 23, 42, 0.1)',
      '0px 16px 24px rgba(15, 23, 42, 0.1)',
      '0px 20px 28px rgba(15, 23, 42, 0.1)',
      '0px 24px 32px rgba(15, 23, 42, 0.12)',
      '0px 28px 36px rgba(15, 23, 42, 0.12)',
      '0px 32px 40px rgba(15, 23, 42, 0.12)',
      '0px 36px 44px rgba(15, 23, 42, 0.12)',
      '0px 40px 48px rgba(15, 23, 42, 0.14)',
      '0px 44px 52px rgba(15, 23, 42, 0.14)',
      '0px 48px 56px rgba(15, 23, 42, 0.14)',
      '0px 52px 60px rgba(15, 23, 42, 0.14)',
      '0px 56px 64px rgba(15, 23, 42, 0.16)',
      '0px 60px 68px rgba(15, 23, 42, 0.16)',
      '0px 64px 72px rgba(15, 23, 42, 0.16)',
      '0px 68px 76px rgba(15, 23, 42, 0.16)',
      '0px 72px 80px rgba(15, 23, 42, 0.18)',
      '0px 76px 84px rgba(15, 23, 42, 0.18)',
      '0px 80px 88px rgba(15, 23, 42, 0.18)',
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '@import': "url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap')",
          body: {
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#F1F5F9',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#CBD5E1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#94A3B8',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 10,
            padding: '10px 20px',
            transition: 'all 0.2s ease-in-out',
          },
          contained: {
            boxShadow: '0px 2px 4px rgba(99, 102, 241, 0.2)',
            '&:hover': {
              boxShadow: '0px 4px 8px rgba(99, 102, 241, 0.3)',
              transform: 'translateY(-1px)',
            },
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
              backgroundColor: 'rgba(99, 102, 241, 0.04)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0px 1px 3px rgba(15, 23, 42, 0.08)',
            border: '1px solid #E2E8F0',
            borderRadius: 16,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0px 4px 12px rgba(15, 23, 42, 0.1)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: '0px 1px 3px rgba(15, 23, 42, 0.08)',
            border: '1px solid #E2E8F0',
            borderRadius: 16,
          },
          elevation0: {
            boxShadow: 'none',
          },
          elevation1: {
            boxShadow: '0px 1px 3px rgba(15, 23, 42, 0.08)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0px 1px 3px rgba(15, 23, 42, 0.06)',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'medium',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              transition: 'all 0.2s ease-in-out',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#94A3B8',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: '2px',
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
          notchedOutline: {
            borderColor: '#E2E8F0',
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            minHeight: '56px',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            minHeight: '56px',
            borderRadius: 10,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: '#F8FAFC',
            '& .MuiTableCell-head': {
              fontWeight: 600,
              color: '#475569',
              fontSize: '0.8125rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              borderBottom: '2px solid #E2E8F0',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: '#F1F5F9',
            padding: '16px',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: '#F8FAFC',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            borderRadius: 8,
          },
          colorSuccess: {
            backgroundColor: '#ECFDF5',
            color: '#059669',
          },
          colorError: {
            backgroundColor: '#FEF2F2',
            color: '#DC2626',
          },
          colorWarning: {
            backgroundColor: '#FFFBEB',
            color: '#D97706',
          },
          colorInfo: {
            backgroundColor: '#EFF6FF',
            color: '#2563EB',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
          standardSuccess: {
            backgroundColor: '#ECFDF5',
            color: '#065F46',
          },
          standardError: {
            backgroundColor: '#FEF2F2',
            color: '#991B1B',
          },
          standardWarning: {
            backgroundColor: '#FFFBEB',
            color: '#92400E',
          },
          standardInfo: {
            backgroundColor: '#EFF6FF',
            color: '#1E40AF',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            boxShadow: '0px 20px 40px rgba(15, 23, 42, 0.15)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.25rem',
            fontWeight: 600,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9375rem',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: '#1E293B',
            fontSize: '0.8125rem',
            borderRadius: 8,
            padding: '8px 12px',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.12)',
            border: '1px solid #E2E8F0',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '4px 8px',
            padding: '10px 16px',
            '&:hover': {
              backgroundColor: '#F1F5F9',
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: '#E2E8F0',
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <AuthProvider>
          <NotificationProvider>
          <Router>
          <Routes>
            {/* Landing Page - Public */}
            <Route
              path="/"
              element={
                <PublicRoute>
                  <LandingPage />
                </PublicRoute>
              }
            />
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmailPrompt />} />
            <Route path="/verify-email/:token" element={<EmailVerification />} />
            <Route path="/terms" element={<TermsAndPrivacy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms-acceptance" element={<TermsAcceptance />} />
            <Route path="/change-password" element={<ChangePassword />} />
            {/* Protected routes */}
            {/* Super Admin Routes */}
            <Route
              path="/super-admin"
              element={
                <ProtectedRoute requireSuperAdmin={true}>
                  <SuperAdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin/business/:businessId"
              element={
                <ProtectedRoute requireSuperAdmin={true}>
                  <BusinessDetailsPage />
                </ProtectedRoute>
              }
            />
            {/* Main App Routes - Protected */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="profile/settings" element={<ProfileSettings />} />
              <Route path="profile/history" element={<ProfileHistory />} />
              <Route path="branches" element={<BranchManagement />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="products" element={
                <ProtectedRoute roles={['admin']}>
                  <ProductManagement />
                </ProtectedRoute>
              } />
              <Route path="categories" element={
                <ProtectedRoute roles={['admin']}>
                  <CategoryManagement />
                </ProtectedRoute>
              } />
              <Route path="branch-inventory" element={
                <ProtectedRoute roles={['admin']}>
                  <BranchInventory />
                </ProtectedRoute>
              } />
              <Route path="inventory" element={
                <ProtectedRoute roles={['admin']}>
                  <InventoryManagement />
                </ProtectedRoute>
              } />
              <Route path="sales" element={
                <ProtectedRoute roles={['admin', 'branch_admin', 'cashier']}>
                  <SalesInterface />
                </ProtectedRoute>
              } />
              <Route path="branches/:branchId" element={<BranchDetails />} />
              <Route path="user-branches/:id" element={
                <ProtectedRoute roles={['admin']}>
                  <UserBranchManagement />
                </ProtectedRoute>
              } />
              <Route path="branch-users/:id" element={
                <ProtectedRoute roles={['admin']}>
                  <BranchUserManagement />
                </ProtectedRoute>
              } />
            </Route>
            {/* Password reset routes */}
            <Route path="/request-password-reset" element={<RequestPasswordReset />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            {/* Article routes */}
            <Route path="/articles/:slug" element={<ArticlePage />} />
          </Routes>
          </Router>
          </NotificationProvider>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;